// ═══════════════════════════════════════════════════════════════════
// ieForm.js — THE FORM: a body's proportions, on the fly (body pass 2, 2026-09-30)
// ═══════════════════════════════════════════════════════════════════
// caP'n: "we are eventually going to make a character creator with 30+ more models … V'ael needs
// to be a good base to where making new ones will 'never' have these problems again and should be
// able to make them on the fly with proper proportions (or disproportions if I ever say make the
// arm gigantic but only one) … or thicken the forearm … put on any article of clothing on any
// character and have it not clip."
//
// WHAT IT IS. A FORM is a few numbers per bone, applied to an IEH1 body AFTER the rig-up (so every
// weight fix — shoulders, pit, hips, skirt — carries over to every shaped body for free: the hard
// rig work happens ONCE, on the base):
//     applyForm(root, { armR: { scale: 1.8 }, forearmL: { girth: [1, 1.4] }, legs: { len: 1.1 } })
//   len    — the bone's length (its child joint moves; what hangs below follows)
//   girth  — thickness round the bone: one number, or [at its head, at its far end] (taper)
//   w, d   — width only (her left-right) / depth only (front-back), × girth
//   scale  — the bone AND EVERYTHING BELOW IT, uniformly (a whole arm, hand and fingers included)
// Groups: arms, armL/R (upperarm+forearm+hand), handL/R, legs, legL/R (thigh+shin+foot), footL/R,
// torso (hips+spine+spine2+chest), head. A group's `scale` goes on its first bone (it carries
// down); its len/girth/w/d go on every bone in it. Bone names win over groups.
//
// HOW (all in her rest pose, before makeRig — the rig then measures the new body by itself:
// IK lengths, colliders, the arm clearance, hand places):
//   · each bone gets a map of rest space: along its axis ×len (inside the bone only), across ×girth
//     (tapered head→tail, ×w / ×d), all ×scale about its NEW head; a child joint's new head is its
//     parent's map of its old one (a wider chest carries the shoulders out; wider hips the legs)
//   · SKIN: each vertex = its weights' blend of those maps (smooth through every joint — the same
//     weights that were tuned for motion); normals by the inverse-transpose; morph deltas (her face)
//     by the linear part
//   · CLOTHES FOLLOW THE BODY, NOT THE BONES: a garment vertex near the skin moves exactly as the
//     skin under it moved (the skin's displacement, read only from skin of the same body region its
//     own weights name — a dress hip never follows a hand hanging beside it), so every gap the
//     sculpt had is kept to the millimetre (a doubled arm gets a doubled sleeve, 3 mm off, not 6);
//     far from the skin (a horn tip, a dread, a skirt hem) it rides its own bones' maps; between
//     2 and 6 cm it blends. Then no cloth may sit closer to the new skin than it did (max 4 mm):
//     a thick thigh pushes a loose skirt out instead of through it.
//   · bones: new positions, new inverse binds (identity rest: IBM_new = IBM_old · T(h_old − h_new))
//   · her feet go back on the floor (longer legs = a taller body, feet at y = the old lowest point)
//   · the rig's own data moves with her: collider radii and points, floor contacts, sockets, chain
//     tips, model_top
// A garment put on LATER (ieRig.attachPart → formPart) is fitted to the same form by the same rule.
// A form of {} (or none) changes nothing — not one byte (THE PARITY LAW).
// ═══════════════════════════════════════════════════════════════════
import * as THREE from "three";

const GROUPS = {
  armL: ["upperarmL", "forearmL", "handL"], armR: ["upperarmR", "forearmR", "handR"],
  legL: ["thighL", "shinL", "footL"], legR: ["thighR", "shinR", "footR"],
  handL: ["handL"], handR: ["handR"], footL: ["footL"], footR: ["footR"],
  torso: ["hips", "spine", "spine2", "chest"], head: ["head"],
};
GROUPS.arms = [...GROUPS.armL, ...GROUPS.armR];
GROUPS.legs = [...GROUPS.legL, ...GROUPS.legR];
const GROUP_ROOTS = { arms: ["upperarmL", "upperarmR"], legs: ["thighL", "thighR"], torso: ["spine"] };   // (torso.scale = from the waist up: on hips it grew the legs too)

const RIDER = /(Twist|Helper)[LR]\d*$/;                  // sub-bones of a limb: they share its map
const NOT_AXIS = /Twist|Helper|^skirt_|^dread|^tail/;      // never a bone's axis child
const SIDED = /[LR]\d*$/;

// the body region a bone belongs to (clothes only follow skin of a region their weights name)
function regionOf(name) {
  if (/^skirt_/.test(name)) return "skirt";
  if (/^tail/.test(name)) return "tail";
  if (/^(head|dread)/.test(name)) return "head";
  const m = /(L|R)\d*$/.exec(name);
  if (/^(upperarm|forearm|hand|index|middle|ring|pinky|thumb|elbowHelper|shoulderHelper|armpitHelper)/.test(name) && m) return "arm" + m[1];
  if (/^(thigh|shin|foot|toe|hipHelper|kneeHelper)/.test(name) && m) return "leg" + m[1];
  return "torso";                                          // root, hips, spine*, chest, clavicles, neck
}
const REGION_OK = { skirt: ["torso", "legL", "legR"] };   // loose cloth on the ring may follow hips AND legs

// ── resolve a form (groups → bones) ──
export function resolveForm(form) {
  const out = {};
  if (!form) return out;
  const put = (b, k, v) => { (out[b] = out[b] || {})[k] = v; };
  for (const [key, f] of Object.entries(form)) {
    if (!f || key[0] === "_" || GROUPS[key] === undefined) continue;
    for (const b of GROUPS[key]) for (const k of ["len", "girth", "w", "d"]) if (f[k] !== undefined) put(b, k, f[k]);
    if (f.scale !== undefined) for (const b of GROUP_ROOTS[key] || [GROUPS[key][0]]) put(b, "scale", f.scale);
  }
  for (const [key, f] of Object.entries(form)) if (f && key[0] !== "_" && GROUPS[key] === undefined) for (const [k, v] of Object.entries(f)) put(key, k, v);
  return out;
}
const isIdentity = (R) => Object.values(R).every((f) => ["len", "w", "d", "scale"].every((k) => f[k] === undefined || f[k] === 1)
  && (f.girth === undefined || f.girth === 1 || (Array.isArray(f.girth) && f.girth.every((x) => x === 1))));

// ── the maps ──
const _q = new THREE.Vector3(), _r = new THREE.Vector3();
function makeMaps(bones, heads, R, drape) {
  // bones: [Bone] parents-first; heads: Map(name → Vector3 old head, bind space)
  const M = new Map();
  const byName = new Map(bones.map((b) => [b.name, b]));
  const axisChild = (b) => {
    let first = null;
    for (const c of b.children) {
      if (!c.isBone || NOT_AXIS.test(c.name) || !heads.has(c.name)) continue;
      if (/^hand/.test(b.name) && /^middle/.test(c.name)) return c;
      if (!SIDED.test(b.name) && !SIDED.test(c.name)) return c;
      if (!first) first = c;
    }
    return /^hand/.test(b.name) ? (b.children.find((c) => /^middle/.test(c.name)) || first) : first;
  };
  for (const b of bones) {
    const h = heads.get(b.name); if (!h) continue;
    const pb = b.parent && b.parent.isBone ? b.parent : null;
    const pm = pb ? M.get(pb.name) : null;
    let owner = b;
    while (RIDER.test(owner.name) && owner.parent && owner.parent.isBone) owner = owner.parent;
    const own = owner !== b ? M.get(owner.name) : null;
    const f = R[own ? owner.name : b.name] || {};
    const S = (pm ? pm.S : 1) * (own ? 1 : (f.scale !== undefined ? f.scale : 1));
    let a, L;
    const c = axisChild(b);
    if (own) { a = own.a; L = own.L; }
    else if (c) { a = heads.get(c.name).clone().sub(h); L = a.length(); a.normalize(); }
    else if (pm) { a = h.clone().sub(heads.get(pb.name)); L = a.length(); if (L < 1e-6) { a = pm.a.clone(); L = pm.L; } else a.normalize(); L *= /^head/.test(b.name) ? 2.4 : 0.8; }
    else { a = new THREE.Vector3(0, 1, 0); L = 0.1; }
    if (L < 1e-6) { a = new THREE.Vector3(0, 1, 0); L = 0.05; }
    // across: depth = her +z made perpendicular to the axis (her +y for a bone that points forward); width = the third, toward her +x
    const dd = new THREE.Vector3(0, 0, 1).addScaledVector(a, -a.z);
    if (dd.lengthSq() < 0.05) dd.set(0, 1, 0).addScaledVector(a, -a.y);
    dd.normalize();
    const ww = new THREE.Vector3().crossVectors(dd, a).normalize();
    if (ww.x < 0) ww.negate();
    const g = own ? null : (f.girth === undefined ? [1, 1] : Array.isArray(f.girth) ? [f.girth[0], f.girth[1] !== undefined ? f.girth[1] : f.girth[0]] : [f.girth, f.girth]);
    const m = own
      ? { ...own, name: b.name, h, S: own.S, rider: owner.name }
      : { name: b.name, h, a, L, ww, dd, len: f.len !== undefined ? f.len : 1, g0: g[0], g1: g[1], w: f.w !== undefined ? f.w : 1, d: f.d !== undefined ? f.d : 1, S };
    m.parent = pb ? pb.name : null;
    M.set(b.name, m);
  }
  // new heads, parents first: a child's new head is its parent's map of its old one
  for (const b of bones) {
    const m = M.get(b.name); if (!m) continue;
    const pm = m.parent ? M.get(m.parent) : null;
    m.h2 = pm ? mapPoint(pm, m.h, new THREE.Vector3()) : m.h.clone();
    if (m.rider) { const o = M.get(m.rider); m.oh = o.h; m.oh2 = null; }
  }
  for (const m of M.values()) if (m.rider) m.oh2 = M.get(m.rider).h2;          // riders map about their owner's heads
  // CLOTHES KEEP THEIR LENGTH (caP'n, 2026-09-30: "ideally they should stretch to keep their length"): cloth that
  // rides the skirt ring hangs from the hips; with longer (or shorter) legs, everything below the hips on the ring
  // stretches down by the LEG ratio — a floor-length dress stays floor-length. (form._drape: false keeps the cloth's own length.)
  const leg = (h, k) => { const t = M.get("thigh" + k), sh = M.get("shin" + k), f = M.get("foot" + k); return t && sh && f ? h(t).distanceTo(h(sh)) + h(sh).distanceTo(h(f)) : 0; };
  const L0 = leg((m) => m.h, "L") + leg((m) => m.h, "R"), L1 = leg((m) => m.h2, "L") + leg((m) => m.h2, "R");
  const hips = M.get("hips");
  if (drape !== false && hips && L0 > 0 && Math.abs(L1 / L0 - 1) > 1e-6) {
    const k = L1 / L0;
    for (const b of bones) {                                         // parents first: a ring joint hangs from its (stretched) parent
      const m = M.get(b.name); if (!m || !/^skirt_/.test(b.name)) continue;
      m.drape = { k, y0: hips.h.y };                                 // (about her old hips height, in the old rest space)
      const pm = m.parent ? M.get(m.parent) : null;
      if (pm) mapPoint(pm, m.h, m.h2);
    }
  }
  M.byName = byName;
  return M;
}
// a point through one bone's map (rest, bind space)
function mapPoint(m, p, out) {
  const h = m.rider ? m.oh : m.h, h2 = m.rider ? (m.oh2 || m.h2) : m.h2;
  _q.copy(p).sub(h);
  if (m.drape) {                       // (clothes keep their length) the offset from this ring joint, stretched in her OLD rest space below the hips
    const D = m.drape, dy = (p.y < D.y0 ? D.y0 + (p.y - D.y0) * D.k : p.y) - (h.y < D.y0 ? D.y0 + (h.y - D.y0) * D.k : h.y);
    _q.y = dy;
  }
  const t = _q.dot(m.a), rw = _q.dot(m.ww), rd = _q.dot(m.dd);
  const tc = Math.min(Math.max(t, 0), m.L), u = m.L > 0 ? tc / m.L : 0, g = m.g0 + (m.g1 - m.g0) * u;
  const t2 = m.len * tc + (t - tc);
  out.copy(h2)
    .addScaledVector(m.a, m.S * t2)
    .addScaledVector(m.ww, m.S * g * m.w * rw)
    .addScaledVector(m.dd, m.S * g * m.d * rd);
  return out;
}
// the linear part (for normals and morph deltas): coefficients along a / ww / dd at a point
function linAt(m, p) {
  const h = m.rider ? m.oh : m.h;
  _r.copy(p).sub(h);
  const t = _r.dot(m.a), tc = Math.min(Math.max(t, 0), m.L), u = m.L > 0 ? tc / m.L : 0, g = m.g0 + (m.g1 - m.g0) * u;
  const inside = t >= 0 && t <= m.L;
  return [m.S * (inside ? m.len : 1), m.S * g * m.w, m.S * g * m.d];
}
const _la = new THREE.Vector3();
function applyLin(m, k, v, out, inv) {
  // v in bind space → components on (a, ww, dd), scaled (or divided: normals), summed into out
  const ca = v.dot(m.a), cw = v.dot(m.ww), cd = v.dot(m.dd);
  if (inv) out.addScaledVector(m.a, ca / k[0]).addScaledVector(m.ww, cw / k[1]).addScaledVector(m.dd, cd / k[2]);
  else out.addScaledVector(m.a, ca * k[0]).addScaledVector(m.ww, cw * k[1]).addScaledVector(m.dd, cd * k[2]);
  return out;
}

// ── a small spatial hash (rest skin) ──
function grid(P, ids, cell) {
  const G = new Map();
  const hk = (i, j, k) => ((i * 73856093) ^ (j * 19349663) ^ (k * 83492791)) | 0;
  for (const i of ids) { const k = hk(Math.floor(P[i * 3] / cell), Math.floor(P[i * 3 + 1] / cell), Math.floor(P[i * 3 + 2] / cell)); let a = G.get(k); if (!a) G.set(k, (a = [])); a.push(i); }
  return {
    // the K nearest points passing ok(j), within maxR: shells of cells grow outward and stop as soon as
    // no unvisited cell can hold anything closer than the K-th found (most cloth lies within a cell or two
    // of the skin: a handful of buckets instead of hundreds — the critic measured 3 s per body before)
    // (bd/bi are NOT reset here: several region grids fill one list — the caller resets)
    knn(x, y, z, K, maxR, bd, bi) {
      const cx = Math.floor(x / cell), cy = Math.floor(y / cell), cz = Math.floor(z / cell), R = Math.ceil(maxR / cell), m2 = maxR * maxR;
      for (let r = 0; r <= R; r++) {
        for (let i = -r; i <= r; i++) for (let j = -r; j <= r; j++) for (let k = -r; k <= r; k++) {
          if (Math.max(Math.abs(i), Math.abs(j), Math.abs(k)) !== r) continue;          // this shell only
          const a = G.get(hk(cx + i, cy + j, cz + k)); if (!a) continue;
          for (const q of a) {
            const dx = P[q * 3] - x, dy = P[q * 3 + 1] - y, dz = P[q * 3 + 2] - z, d = dx * dx + dy * dy + dz * dz;
            if (d >= bd[K - 1] || d > m2) continue;
            let s = K - 1; while (s > 0 && bd[s - 1] > d) { bd[s] = bd[s - 1]; bi[s] = bi[s - 1]; s--; }
            bd[s] = d; bi[s] = q;
          }
        }
        const reach = r * cell;                        // anything in shell r+1 is at least this far
        if (bi[K - 1] >= 0 && bd[K - 1] <= reach * reach) break;
      }
    },
  };
}

// dominant-weight bone of each vertex, and the set of regions a vertex's weights name
function vertexBones(geo, bones) {
  const SI = geo.attributes.skinIndex, SW = geo.attributes.skinWeight, n = SI.count;
  const dom = new Int32Array(n), regs = new Array(n);
  for (let i = 0; i < n; i++) {
    let best = -1, bj = 0; const rs = new Set();
    for (let k = 0; k < 4; k++) {
      const w = SW.getComponent(i, k); if (w <= 0) continue;
      const b = bones[SI.getComponent(i, k)]; if (!b) continue;
      if (w > best) { best = w; bj = SI.getComponent(i, k); }
      if (w > 0.05) { const r = regionOf(b.name); rs.add(r); for (const x of REGION_OK[r] || []) rs.add(x); }
    }
    dom[i] = bj; regs[i] = rs;
  }
  return { dom, regs };
}

// deform one skinned mesh's rest geometry by its weights through the maps (skin, and a garment's far part)
function weightMap(geo, bones, M, out, nOut, dy) {
  const P = geo.attributes.position, N = geo.attributes.normal, SI = geo.attributes.skinIndex, SW = geo.attributes.skinWeight;
  const n = P.count, p = new THREE.Vector3(), q = new THREE.Vector3(), acc = new THREE.Vector3(), nv = new THREE.Vector3(), na = new THREE.Vector3();
  for (let i = 0; i < n; i++) {
    p.fromBufferAttribute(P, i); acc.set(0, 0, 0); na.set(0, 0, 0);
    if (N) nv.fromBufferAttribute(N, i);
    let ws = 0;
    for (let k = 0; k < 4; k++) {
      const w = SW.getComponent(i, k); if (w <= 0) continue;
      const b = bones[SI.getComponent(i, k)], m = b && M.get(b.name); if (!m) continue;
      mapPoint(m, p, q); acc.addScaledVector(q, w); ws += w;
      if (N) { _la.set(0, 0, 0); applyLin(m, linAt(m, p), nv, _la, true); na.addScaledVector(_la, w); }
    }
    if (ws > 0) acc.multiplyScalar(1 / ws); else acc.copy(p);
    acc.y += dy;
    out[i * 3] = acc.x; out[i * 3 + 1] = acc.y; out[i * 3 + 2] = acc.z;
    if (N && nOut) { if (na.lengthSq() < 1e-20) na.copy(nv); na.normalize(); nOut[i * 3] = na.x; nOut[i * 3 + 1] = na.y; nOut[i * 3 + 2] = na.z; }
  }
}
function morphMap(geo, bones, M) {
  const MA = geo.morphAttributes && geo.morphAttributes.position; if (!MA || !MA.length) return;
  if (!geo.morphTargetsRelative) { console.warn("[ieForm] absolute morph targets are not formed (glTF morphs are relative)"); return; }
  const P = geo.attributes.position, SI = geo.attributes.skinIndex, SW = geo.attributes.skinWeight;
  const p = new THREE.Vector3(), v = new THREE.Vector3(), acc = new THREE.Vector3();
  const out = MA.map((a) => new THREE.BufferAttribute(new Float32Array(a.array.length), 3));
  for (let i = 0; i < P.count; i++) {
    p.fromBufferAttribute(P, i);
    const ks = [];
    for (let k = 0; k < 4; k++) { const w = SW.getComponent(i, k); if (w <= 0) continue; const b = bones[SI.getComponent(i, k)], m = b && M.get(b.name); if (m) ks.push([m, linAt(m, p), w]); }
    for (let t = 0; t < MA.length; t++) {
      v.fromBufferAttribute(MA[t], i);
      if (v.x === 0 && v.y === 0 && v.z === 0) continue;
      acc.set(0, 0, 0); let ws = 0;
      for (const [m, k, w] of ks) { _la.set(0, 0, 0); applyLin(m, k, v, _la, false); acc.addScaledVector(_la, w); ws += w; }
      if (ws > 0) acc.multiplyScalar(1 / ws); else acc.copy(v);
      out[t].setXYZ(i, acc.x, acc.y, acc.z);
    }
  }
  geo.morphAttributes.position = out;
}
function vnormals(P, idx, n) {
  const N = new Float32Array(n * 3);
  for (let t = 0; t < idx.length; t += 3) {
    const a = idx[t] * 3, b = idx[t + 1] * 3, c = idx[t + 2] * 3;
    const ux = P[b] - P[a], uy = P[b + 1] - P[a + 1], uz = P[b + 2] - P[a + 2], vx = P[c] - P[a], vy = P[c + 1] - P[a + 1], vz = P[c + 2] - P[a + 2];
    const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
    for (const q of [a, b, c]) { N[q] += nx; N[q + 1] += ny; N[q + 2] += nz; }
  }
  for (let i = 0; i < n; i++) { const l = Math.hypot(N[i * 3], N[i * 3 + 1], N[i * 3 + 2]) || 1; N[i * 3] /= l; N[i * 3 + 1] /= l; N[i * 3 + 2] /= l; }
  return N;
}

// ── THE FITTER: the skin before/after, for clothes (the body's own and any worn later) ──
function makeFitter(skinMesh, skinP1, M, dy) {
  const g = skinMesh.geometry, P0 = g.attributes.position.array, n = g.attributes.position.count;
  const idx = g.index ? g.index.array : Uint32Array.from({ length: n }, (_, i) => i);
  const N0 = vnormals(P0, idx, n), N1 = vnormals(skinP1, idx, n);
  const { dom } = vertexBones(g, skinMesh.skeleton.bones);        // a skin vertex's region = its dominant bone's
  const reg = new Array(n);
  for (let i = 0; i < n; i++) { const b = skinMesh.skeleton.bones[dom[i]]; reg[i] = b ? regionOf(b.name) : "torso"; }
  // one small search grid per body region (a dread only ever looks at head skin: filtering a shared grid
  // point by point cost 1.6 s a body), fine for the fit, coarse for the far clearance check
  const byReg = {};
  for (let i = 0; i < n; i++) (byReg[reg[i]] = byReg[reg[i]] || []).push(i);
  const G = {}, Gf = {};
  // (the coarse grid holds every 6th skin point: the far check only needs "roughly how close is her skin" — a horn
  //  tip scanning her whole head's skin at full density cost 200 ms a body)
  for (const [r, ids] of Object.entries(byReg)) { G[r] = grid(P0, ids, 0.02); Gf[r] = grid(P0, ids.filter((_, k) => k % 6 === 0), 0.06); }
  // a region whose skin only SHIFTED as a whole (or not at all: dy, the floor) — cloth that follows only such
  // regions takes that shift with no search and no gap can change (one arm edited: the rest of her is free)
  const shift = {};
  for (const [r, ids] of Object.entries(byReg)) {
    const j0 = ids[0], sx = skinP1[j0 * 3] - P0[j0 * 3], sy = skinP1[j0 * 3 + 1] - P0[j0 * 3 + 1], sz = skinP1[j0 * 3 + 2] - P0[j0 * 3 + 2];
    let rigid = true;
    for (const j of ids) if (Math.abs(skinP1[j * 3] - P0[j * 3] - sx) + Math.abs(skinP1[j * 3 + 1] - P0[j * 3 + 1] - sy) + Math.abs(skinP1[j * 3 + 2] - P0[j * 3 + 2] - sz) > 1e-6) { rigid = false; break; }
    shift[r] = rigid ? [sx, sy, sz] : null;
  }
  return { P0, P1: skinP1, N0, N1, n, reg, G, Gf, M, dy, shift };
}
const REACH = 0.06, NEAR = 0.02, FAR = 0.06, KEEP = 0.004, CLEAR_REACH = 0.16;
const smooth = (a, b, x) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };
// fit one garment mesh (its rest positions, normals, morphs) to the formed skin
function fitGarment(F, geo, bones) {
  const P = geo.attributes.position, n = P.count, A = P.array;
  const out = new Float32Array(n * 3), nOut = geo.attributes.normal ? new Float32Array(n * 3) : null;
  weightMap(geo, bones, F.M, out, nOut, 0);                       // the far answer: its own bones (the maps already stand on the new floor)
  const { regs } = vertexBones(geo, bones);
  const K = 6, bd = new Float64Array(K), bi = new Int32Array(K), bd1 = new Float64Array(1), bi1 = new Int32Array(1);
  let moved = 0, pushed = 0;
  for (let i = 0; i < n; i++) {
    const x = A[i * 3], y = A[i * 3 + 1], z = A[i * 3 + 2], rs = regs[i];
    let sh = null, same = rs.size > 0;
    for (const r of rs) { const q = F.shift[r]; if (!q || (sh && (q[0] !== sh[0] || q[1] !== sh[1] || q[2] !== sh[2]))) { same = false; break; } sh = q; }
    if (same && sh) {                                          // every region it follows only shifted: so does it (bones AND field agree)
      out[i * 3] = x + sh[0]; out[i * 3 + 1] = y + sh[1]; out[i * 3 + 2] = z + sh[2];
      continue;
    }
    bd.fill(Infinity); bi.fill(-1);
    for (const r of rs) if (F.G[r]) F.G[r].knn(x, y, z, K, REACH, bd, bi);
    if (bi[0] < 0) {                                           // far from any skin of its regions (a hem, a horn tip): its bones; then the clearance
      bd1.fill(Infinity); bi1.fill(-1);
      for (const r of rs) if (F.Gf[r]) F.Gf[r].knn(x, y, z, 1, CLEAR_REACH, bd1, bi1);
      if (bi1[0] >= 0 && keepClear(F, A, out, i, bi1[0])) pushed++;
      continue;
    }
    const d0 = Math.sqrt(bd[0]);
    const t = 1 - smooth(NEAR, FAR, d0);                     // 1 = rides the skin's displacement, 0 = its own bones
    let fx = 0, fy = 0, fz = 0, ws = 0;
    for (let k = 0; k < K && bi[k] >= 0; k++) {
      const j = bi[k], w = 1 / (Math.sqrt(bd[k]) + 0.002) ** 2;
      fx += w * (F.P1[j * 3] - F.P0[j * 3]); fy += w * (F.P1[j * 3 + 1] - F.P0[j * 3 + 1]); fz += w * (F.P1[j * 3 + 2] - F.P0[j * 3 + 2]); ws += w;
    }
    if (t > 0 && ws > 0) {
      out[i * 3] = (1 - t) * out[i * 3] + t * (x + fx / ws);
      out[i * 3 + 1] = (1 - t) * out[i * 3 + 1] + t * (y + fy / ws);
      out[i * 3 + 2] = (1 - t) * out[i * 3 + 2] + t * (z + fz / ws);
      moved++;
    }
    if (keepClear(F, A, out, i, bi[0])) pushed++;
  }
  return { out, nOut, moved, pushed };
}
// NO NEW CLIPPING: the cloth may not sit closer to the new skin than it did (up to KEEP), along skin vertex j's normal
function keepClear(F, A, out, i, j) {
  const x = A[i * 3], y = A[i * 3 + 1], z = A[i * 3 + 2];
  const g0 = (x - F.P0[j * 3]) * F.N0[j * 3] + (y - F.P0[j * 3 + 1]) * F.N0[j * 3 + 1] + (z - F.P0[j * 3 + 2]) * F.N0[j * 3 + 2];
  if (g0 <= -0.001) return false;                              // (it was already inside at rest: the sculpt's business)
  const want = Math.min(g0, KEEP);
  const g1 = (out[i * 3] - F.P1[j * 3]) * F.N1[j * 3] + (out[i * 3 + 1] - F.P1[j * 3 + 1]) * F.N1[j * 3 + 1] + (out[i * 3 + 2] - F.P1[j * 3 + 2]) * F.N1[j * 3 + 2];
  if (g1 >= want) return false;
  const push = want - g1;
  out[i * 3] += F.N1[j * 3] * push; out[i * 3 + 1] += F.N1[j * 3 + 1] * push; out[i * 3 + 2] += F.N1[j * 3 + 2] * push;
  return true;
}

// ── rebind: bone positions + inverse binds from the new heads ──
const _T = new THREE.Matrix4();
function rebindSkeleton(sk, M, heads2) {
  // ⚠ SkeletonUtils.clone → Skeleton.clone() SHARES boneInverses (the array and its matrices) with the loaded
  // scene and every sibling body: writing them in place formed EVERY V'ael (critic, 2026-09-30: an unformed
  // sibling skinned 17 cm off its rest). Each formed skeleton owns fresh copies.
  sk.boneInverses = sk.boneInverses.map((m) => m.clone());
  for (let i = 0; i < sk.bones.length; i++) {
    const b = sk.bones[i], m = M.get(b.name); if (!m) continue;
    const h1 = new THREE.Vector3().setFromMatrixPosition(sk.boneInverses[i].clone().invert());
    const h2 = heads2.get(b.name) || h1;
    sk.boneInverses[i].multiply(_T.makeTranslation(h1.x - h2.x, h1.y - h2.y, h1.z - h2.z));
  }
}

// ═══ the public door ═══
// root: a freshly prepared body (cloneSkinned, NOT yet rigged). Returns a report, or null if no form.
// opts.ground (default true): her lowest point stays where it was.
export function applyForm(root, form, opts = {}) {
  if (root.userData && root.userData.form) { console.warn("[ieForm] this body is already formed — prepare a fresh copy for a new form (forms don't stack)"); return null; }
  const R = resolveForm(form);
  if (!Object.keys(R).length || isIdentity(R)) return null;
  const meshes = [];
  root.traverse((o) => { if (o.isSkinnedMesh && !/_ink$/.test(o.name)) meshes.push(o); });
  if (!meshes.length) return null;
  const skin = meshes.find((m) => /_skin$/.test(m.name)) || meshes.reduce((a, b) => (b.geometry.attributes.position.count > a.geometry.attributes.position.count ? b : a));
  const sk = skin.skeleton;
  // old heads (bind space) from her inverse binds
  const heads = new Map();
  sk.bones.forEach((b, i) => heads.set(b.name, new THREE.Vector3().setFromMatrixPosition(sk.boneInverses[i].clone().invert())));
  const order = []; root.traverse((o) => { if (o.isBone && heads.has(o.name)) order.push(o); });
  for (const k of Object.keys(R)) if (!heads.has(k)) { console.warn("[ieForm] no bone or group called '" + k + "' — ignored"); delete R[k]; }
  if (!Object.keys(R).length || isIdentity(R)) return null;
  const M = makeMaps(order, heads, R, form && form._drape);
  // skin first (without grounding), to find the floor shift
  const sg = skin.geometry, sn = sg.attributes.position.count;
  const sP = new Float32Array(sn * 3), sN = sg.attributes.normal ? new Float32Array(sn * 3) : null;
  weightMap(sg, sk.bones, M, sP, sN, 0);
  let dy = 0;
  if (opts.ground !== false) {
    let lo0 = Infinity, lo1 = Infinity;
    for (const m of meshes) {
      const g = m.geometry, P = g.attributes.position, tmp = m === skin ? sP : new Float32Array(P.count * 3);
      if (m !== skin) weightMap(g, m.skeleton.bones, M, tmp, null, 0);
      for (let i = 0; i < P.count; i++) { if (P.getY(i) < lo0) lo0 = P.getY(i); if (tmp[i * 3 + 1] < lo1) lo1 = tmp[i * 3 + 1]; }
    }
    dy = lo0 - lo1;
    for (let i = 0; i < sn; i++) sP[i * 3 + 1] += dy;
  }
  for (const m of M.values()) { m.h2.y += dy; }               // every new head rides the floor shift (riders read their owner's)
  const F = makeFitter(skin, sP, M, dy);
  const rep = { bones: Object.keys(R), meshes: {}, dy };
  const geos = [];
  for (const m of meshes) {
    const g0 = m.geometry, g = g0.clone();
    let P1, N1;
    if (m === skin) { P1 = sP; N1 = sN; morphMap(g, sk.bones, M); rep.meshes[m.name] = "skin"; }
    else { const _t0 = performance.now(); const r = fitGarment(F, g0, m.skeleton.bones); if (typeof process !== "undefined" && process.env && process.env.FORM_PROF) console.log("  fit", m.name, g0.attributes.position.count, (performance.now() - _t0).toFixed(0) + "ms"); P1 = r.out; N1 = r.nOut; morphMap(g, m.skeleton.bones, M); rep.meshes[m.name] = { fit: r.moved, pushed: r.pushed }; }
    g.setAttribute("position", new THREE.BufferAttribute(P1, 3));
    if (N1) g.setAttribute("normal", new THREE.BufferAttribute(N1, 3));
    g.computeBoundingBox(); g.computeBoundingSphere();
    m.geometry = g; geos.push(g);
  }
  // bones: positions from the new heads (identity rest: a local offset = child head − parent head)
  const heads2 = new Map([...M.values()].map((m) => [m.name, m.h2]));
  for (const b of order) {
    const m = M.get(b.name), pm = m.parent ? M.get(m.parent) : null;
    if (pm) b.position.copy(m.h2).sub(pm.h2);
    else b.position.add(_q.copy(m.h2).sub(m.h));          // the top joint carries the floor shift
  }
  const done = new Set();
  for (const m of meshes) if (!done.has(m.skeleton)) { done.add(m.skeleton); rebindSkeleton(m.skeleton, M, heads2); }
  root.updateMatrixWorld(true);
  // the rig's own data
  let ieh = null; root.traverse((o) => { if (!ieh && o.userData && o.userData.ieh) ieh = o.userData.ieh; });
  if (ieh) formIeh(ieh, M, sk.bones);
  Object.defineProperty(root.userData, "form", { value: { form, R, M, F, heads2, geos }, enumerable: false, writable: true, configurable: true });
  return rep;
}

function formIeh(ieh, M, skBones) {
  const v = new THREE.Vector3(), w = new THREE.Vector3();
  // (every new head already carries the floor shift: a map's output is on the new floor)
  const offOf = (bn, off) => { const m = M.get(bn); if (!m) return off; v.set(off[0], off[1], off[2]).add(m.h); mapPoint(m, v, w); w.sub(m.h2); return [w.x, w.y, w.z].map((x) => +x.toFixed(5)); };
  const ptOf = (bn, p) => { const m = M.get(bn); if (!m) return p; v.set(p[0], p[1], p[2]); mapPoint(m, v, w); return [w.x, w.y, w.z].map((x) => +x.toFixed(5)); };
  const girthOf = (bn) => { const m = M.get(bn); if (!m) return 1; const mm = m.rider ? M.get(m.rider) : m; return mm.S * 0.5 * (mm.g0 + mm.g1) * Math.sqrt(mm.w * mm.d); };
  for (const c of ieh.colliders || []) {
    c.r = +(c.r * girthOf(c.a)).toFixed(5);
    if (c.pa) c.pa = offOf(c.a, c.pa);
    if (c.pb) c.pb = offOf(c.b, c.pb);
  }
  for (const c of ieh.contacts || []) { const b = skBones[c[0]]; if (b) { const o = offOf(b.name, [c[1], c[2], c[3]]); c[1] = o[0]; c[2] = o[1]; c[3] = o[2]; } }
  for (const [k, s] of Object.entries(ieh.sockets || {})) ieh.sockets[k] = [s[0], offOf(s[0], s[1])];
  for (const F of Object.values(ieh.chains || {})) for (const C of Object.values(F.chains || {})) if (C.tip && C.joints) C.tip = ptOf(C.joints[C.joints.length - 1], C.tip);
  const top0 = ieh.model_top || 0;
  if (ieh.model_top && M.get("head")) ieh.model_top = +ptOf("head", [0, ieh.model_top, M.get("head").h.z])[1].toFixed(4);
  // THE REFERENCE the moves were written for (ieRig.js FIT: moves fit the body): her unformed measures
  const m0 = (n) => M.get(n) && M.get(n).h;
  if (["thighL", "shinL", "footL", "thighR", "shinR", "footR"].every(m0)) {
    const leg = (k) => m0("thigh" + k).distanceTo(m0("shin" + k)) + m0("shin" + k).distanceTo(m0("foot" + k));
    ieh.ref = { leg: +(0.5 * (leg("L") + leg("R"))).toFixed(5), top: top0, chest: m0("chest") ? m0("chest").toArray().map((x) => +x.toFixed(5)) : null };
  }
  ieh.form = true;
}

// ── a garment put on LATER (ieRig.attachPart): fit its mesh to this body's form, and give its
// skeleton the body's new binds (so it can share her skeleton) — grafted bones get new rest offsets ──
const FORMED_INFO = new WeakSet();
export function formPart(root, mesh, grafted = [], info = null) {
  const fm = root.userData && root.userData.form; if (!fm) return false;
  const sk = mesh.skeleton, M = fm.M;
  sk.boneInverses = sk.boneInverses.map((m) => m.clone());   // (the part file's binds are shared with its loaded scene — never write them)
  // a bone only this part brings (a bow's, a ponytail's): its map is its parent's, carried on
  for (const b of grafted) {
    if (M.get(b.name)) continue;
    const i = sk.bones.indexOf(b);
    const pm = b.parent && M.get(b.parent.name); if (!pm) continue;
    const h = i >= 0 ? new THREE.Vector3().setFromMatrixPosition(sk.boneInverses[i].clone().invert()) : pm.h.clone().add(b.position);
    const h2 = mapPoint(pm, h, new THREE.Vector3());
    M.set(b.name, { name: b.name, h, h2, a: pm.a, L: 0.05, ww: pm.ww, dd: pm.dd, len: 1, g0: 1, g1: 1, w: 1, d: 1, S: pm.S, parent: pm.name });
    b.position.copy(h2).sub(pm.h2);
    fm.heads2.set(b.name, h2);
  }
  const g0 = mesh.geometry, g = g0.clone();
  const r = fitGarment(fm.F, g0, sk.bones);
  morphMap(g, sk.bones, M);
  g.setAttribute("position", new THREE.BufferAttribute(r.out, 3));
  if (r.nOut) g.setAttribute("normal", new THREE.BufferAttribute(r.nOut, 3));
  g.computeBoundingBox(); g.computeBoundingSphere();
  mesh.geometry = g; fm.geos.push(g);
  rebindSkeleton(sk, M, fm.heads2);
  // the chains the part brings (a bow, a ponytail): their tips are rest points in model space too — formed ONCE per part
  if (info && info.chains && !FORMED_INFO.has(info)) {
    FORMED_INFO.add(info);
    const v = new THREE.Vector3();
    for (const Fm of Object.values(info.chains)) for (const C of Object.values(Fm.chains || {})) {
      const m = C.tip && C.joints && M.get(C.joints[C.joints.length - 1]); if (!m) continue;
      mapPoint(m, v.set(C.tip[0], C.tip[1], C.tip[2]), v); C.tip = [v.x, v.y, v.z].map((x) => +x.toFixed(5));
    }
  }
  return true;
}
